import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../shared/storage/firebase';
import { createDateInZone, DEFAULT_TZ, formatTimeInZone } from '../../../shared/utils/timezone';
import {
  AvailabilityQuarterKey,
  AvailabilityMaybeReason,
  AvailabilityStatus,
  AvailabilityWeekTemplate,
  TeacherAvailabilityQuarterTemplate,
  Squad,
  User,
} from '../../../types';
import '../styles/TeacherAvailabilityAdminPage.css';

type ViewMode = 'coverage' | 'teachers';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

type DayDef = {
  key: DayKey;
  label: string;
};

const DAYS: DayDef[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

type QuarterDef = {
  key: AvailabilityQuarterKey;
  label: string;
  startDate: string; // YYYY-MM-DD in CT
  endDate: string;   // YYYY-MM-DD in CT
};

// Keep in sync with current `AvailabilityQuarterKey` union.
const QUARTERS: QuarterDef[] = [
  { key: 'winter2026', label: 'Winter 2026', startDate: '2026-01-01', endDate: '2026-03-31' },
  { key: 'spring2026', label: 'Spring 2026', startDate: '2026-04-01', endDate: '2026-06-30' },
  { key: 'summer2026', label: 'Summer 2026', startDate: '2026-07-01', endDate: '2026-08-31' },
  { key: 'fall2026', label: 'Fall 2026', startDate: '2026-09-01', endDate: '2026-12-31' },
  { key: 'winter2027', label: 'Winter 2027', startDate: '2027-01-01', endDate: '2027-03-31' },
  { key: 'spring2027', label: 'Spring 2027', startDate: '2027-04-01', endDate: '2027-06-30' },
];

type Slot = {
  dayKey: DayKey;
  start: string; // HH:mm
  end: string;   // HH:mm
};

type TeacherScheduleEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  squadId: string;
  role: 'lead' | 'support' | 'sub_away';
  subStatus: 'none' | 'requested' | 'assigned' | 'canceled';
  subRequestReason: string;
  subRequestNote: string;
  subRequestedByTeacherId: string;
  subAssignedTeacherId: string;
  subOriginalMainTeacherId: string;
  requestedRole: 'lead' | 'co_teacher' | '';
  requestedTeacherId: string;
};

const SUB_REQUEST_REASONS: Record<string, string> = {
  schedule_conflict: 'Schedule conflict',
  illness: 'Illness',
  travel: 'Travel',
  other: 'Other',
};

const MAYBE_REASONS: Array<{ value: AvailabilityMaybeReason; label: string }> = [
  { value: 'schedule_conflict', label: 'Schedule conflict' },
  { value: 'travel', label: 'Travel' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'health', label: 'Health' },
  { value: 'other', label: 'Other' },
];

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function defaultDayCT(dayKey: DayKey) {
  void dayKey;
  const start = '05:00';
  const end = '23:00';
  return {
    status: 'unavailable' as AvailabilityStatus,
    maybeReason: null as AvailabilityMaybeReason | null,
    maybeNote: '',
    timeDetailsEnabled: true,
    blocks: [{ start, end, status: 'unavailable' as AvailabilityStatus }],
  };
}

function defaultWeekTemplateCT(): AvailabilityWeekTemplate {
  return {
    mon: defaultDayCT('mon') as any,
    tue: defaultDayCT('tue') as any,
    wed: defaultDayCT('wed') as any,
    thu: defaultDayCT('thu') as any,
    fri: defaultDayCT('fri') as any,
    sat: defaultDayCT('sat') as any,
    sun: defaultDayCT('sun') as any,
  } as any;
}

function maybeTooltip(reason: AvailabilityMaybeReason | null, note: string) {
  const reasonLabel = reason ? (MAYBE_REASONS.find(x => x.value === reason)?.label || reason) : '';
  const noteText = String(note || '').trim();
  return (
    <span className="ogta-tooltip-pop" role="tooltip">
      <div className="ogta-tooltip-title">MAYBE availability</div>
      <div className="ogta-tooltip-line">
        <b>Reason:</b> {reasonLabel || '—'}
      </div>
      <div className="ogta-tooltip-line">
        <b>Note:</b> {noteText || '—'}
      </div>
    </span>
  );
}

function subTooltip(title: string, reason: string, note: string, extraLabel?: string, extraValue?: string) {
  const reasonLabel = reason ? (SUB_REQUEST_REASONS[reason] || reason) : '';
  const noteText = String(note || '').trim();
  return (
    <span className="ogta-tooltip-pop" role="tooltip">
      <div className="ogta-tooltip-title">{title}</div>
      <div className="ogta-tooltip-line">
        <b>Reason:</b> {reasonLabel || '—'}
      </div>
      <div className="ogta-tooltip-line">
        <b>Note:</b> {noteText || '—'}
      </div>
      {extraLabel && (
        <div className="ogta-tooltip-line">
          <b>{extraLabel}:</b> {extraValue || '—'}
        </div>
      )}
    </span>
  );
}

function minutes(t: string): number {
  const [hh, mm] = String(t || '').split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  return hh * 60 + mm;
}

function isValidTime(t: string): boolean {
  const m = minutes(t);
  return Number.isFinite(m) && m >= 0 && m < 24 * 60;
}

function normalizeTimeRange(start: string, end: string): { start: string; end: string } {
  if (!isValidTime(start) || !isValidTime(end)) return { start, end };
  const startM = minutes(start);
  const endM = minutes(end);
  if (startM < endM) return { start, end };
  const [endHourRaw, endMinuteRaw] = end.split(':').map(Number);
  if (endHourRaw > 0 && endHourRaw < 12) {
    const pmEndM = endM + 12 * 60;
    if (startM < pmEndM) {
      return {
        start,
        end: `${String(endHourRaw + 12).padStart(2, '0')}:${String(endMinuteRaw || 0).padStart(2, '0')}`,
      };
    }
  }
  return { start, end };
}

function fmtTime(t: string): string {
  if (!isValidTime(t)) return t;
  const d = createDateInZone('2026-01-01', t, DEFAULT_TZ);
  return formatTimeInZone(d, DEFAULT_TZ, 'h:mm a');
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  const as = minutes(aStart);
  const ae = minutes(aEnd);
  const bs = minutes(bStart);
  const be = minutes(bEnd);
  if (![as, ae, bs, be].every(Number.isFinite)) return false;
  return as < be && bs < ae;
}

function normalizeDayOfWeekToKey(v: unknown): DayKey | null {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  if (!s) return null;
  const t = s.slice(0, 3);
  const map: Record<string, DayKey> = {
    mon: 'mon',
    tue: 'tue',
    wed: 'wed',
    thu: 'thu',
    fri: 'fri',
    sat: 'sat',
    sun: 'sun',
  };
  return map[t] || null;
}

function startOfWeekMonday(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() - ((next.getDay() + 6) % 7));
  return next;
}

function addDaysLocal(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function ymdLocal(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function normalizeTemplateForQuarter(teacher: User, q: AvailabilityQuarterKey): TeacherAvailabilityQuarterTemplate | null {
  const raw = (teacher as any)?.availabilityTemplates?.[q] as TeacherAvailabilityQuarterTemplate | undefined;
  if (!raw?.week) return null;
  return raw;
}

function getDayFromWeek(week: AvailabilityWeekTemplate, dayKey: DayKey) {
  // types include sat/sun too; map DayKey to AvailabilityWeekTemplate keys
  return (week as any)?.[dayKey] as any;
}

function getDayRange(
  week: AvailabilityWeekTemplate,
  dayKey: DayKey
): {
  status: AvailabilityStatus;
  start: string;
  end: string;
  maybeReason: AvailabilityMaybeReason | null;
  maybeNote: string;
  hasTemplate: boolean;
} {
  const day = getDayFromWeek(week, dayKey);
  const status = (day?.status as AvailabilityStatus) || 'unavailable';
  const firstBlock = Array.isArray(day?.blocks) ? day.blocks[0] : null;
  const start = typeof firstBlock?.start === 'string' ? firstBlock.start : '00:00';
  const end = typeof firstBlock?.end === 'string' ? firstBlock.end : '00:00';
  const maybeReason = (day?.maybeReason ?? null) as AvailabilityMaybeReason | null;
  const maybeNote = String(day?.maybeNote || '');
  return { status, start, end, maybeReason, maybeNote, hasTemplate: true };
}

function computeWeeklyHours(week: AvailabilityWeekTemplate | null): number {
  if (!week) return 0;
  let totalMin = 0;
  for (const d of DAYS) {
    const day = getDayFromWeek(week, d.key);
    const st = (day?.status as AvailabilityStatus) || 'unavailable';
    if (st === 'unavailable') continue;
    const firstBlock = Array.isArray(day?.blocks) ? day.blocks[0] : null;
    const s = typeof firstBlock?.start === 'string' ? minutes(firstBlock.start) : NaN;
    const e = typeof firstBlock?.end === 'string' ? minutes(firstBlock.end) : NaN;
    if (!Number.isFinite(s) || !Number.isFinite(e) || !(s < e)) continue;
    totalMin += (e - s);
  }
  return Math.round((totalMin / 60) * 10) / 10;
}

function chicagoDateKey(d: Date): string {
  return formatTimeInZone(d, DEFAULT_TZ, 'yyyy-MM-dd');
}

function dayKeyFromDate(d: Date): DayKey {
  const dow = formatTimeInZone(d, DEFAULT_TZ, 'EEE').toLowerCase();
  const map: Record<string, DayKey> = {
    mon: 'mon',
    tue: 'tue',
    wed: 'wed',
    thu: 'thu',
    fri: 'fri',
    sat: 'sat',
    sun: 'sun',
  };
  return map[dow] || 'mon';
}

function exceptionStatus(raw: any): AvailabilityStatus | null {
  const s = raw?.status;
  if (s === 'available' || s === 'maybe' || s === 'unavailable') return s;
  // legacy support
  if (raw?.type === 'unavailable') return 'unavailable';
  return null;
}

function exceptionRange(raw: any): { start?: string; end?: string } {
  const start = typeof raw?.start === 'string' ? raw.start : undefined;
  const end = typeof raw?.end === 'string' ? raw.end : undefined;
  return { start, end };
}

function inferStateFromLocationText(v: unknown): string | null {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return null;

  // Heuristic: take last comma-separated segment and look for a 2-letter state code.
  // Examples:
  // - "Lincoln Park, Chicago, IL" -> IL
  // - "Boston, MA" -> MA
  const last = s.split(',').map(p => p.trim()).filter(Boolean).at(-1) || '';
  const m = last.match(/\b([A-Za-z]{2})\b/);
  if (!m) return null;
  return m[1].toUpperCase();
}

type TableTz =
  | 'America/New_York'
  | 'America/Chicago'
  | 'America/Denver'
  | 'America/Phoenix'
  | 'America/Los_Angeles'
  | 'America/Anchorage'
  | 'Pacific/Honolulu';

const TABLE_TZS: Array<{ tz: TableTz; label: string }> = [
  { tz: 'America/New_York', label: 'ET' },
  { tz: 'America/Chicago', label: 'CT' },
  { tz: 'America/Denver', label: 'MT' },
  { tz: 'America/Phoenix', label: 'AZ' },
  { tz: 'America/Los_Angeles', label: 'PT' },
  { tz: 'America/Anchorage', label: 'AK' },
  { tz: 'Pacific/Honolulu', label: 'HI' },
];

function tzLabel(tz: string): string {
  return TABLE_TZS.find(x => x.tz === tz)?.label || tz;
}

function currentAvailabilityQuarterKey(d = new Date()): AvailabilityQuarterKey {
  const dateKey = chicagoDateKey(d);
  return QUARTERS.find(q => dateKey >= q.startDate && dateKey <= q.endDate)?.key || 'winter2026';
}

function convertTimeHHmm(timeHHmm: string, fromTz: string, toTz: string, anchorDate: string): string {
  if (!isValidTime(timeHHmm)) return timeHHmm;
  const utc = createDateInZone(anchorDate, timeHHmm, fromTz);
  return formatTimeInZone(utc, toTz, 'HH:mm');
}

function fmtTimeInTz(timeHHmm: string, toTz: string, anchorDate: string): string {
  if (!isValidTime(timeHHmm)) return timeHHmm;
  const utc = createDateInZone(anchorDate, timeHHmm, toTz);
  return formatTimeInZone(utc, toTz, 'h:mm a');
}

const TeacherAvailabilityAdminPage: React.FC = () => {
  const navigate = useNavigate();

  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [loadingSquads, setLoadingSquads] = useState(true);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [loadingAllEvents, setLoadingAllEvents] = useState(true);
  const [view, setView] = useState<ViewMode>('teachers');
  const [quarterKey, setQuarterKey] = useState<AvailabilityQuarterKey>(() => currentAvailabilityQuarterKey());

  const [tableTz, setTableTz] = useState<TableTz>('America/Chicago');

  // Time window controls (admin-configurable; not hardcoded to 2–6)
  const [gridStart, setGridStart] = useState('05:00');
  const [gridEnd, setGridEnd] = useState('23:00');
  const [stepMinutes, setStepMinutes] = useState(60);
  const [showWeekends, setShowWeekends] = useState(false);

  // Filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Multi-select: empty selection means "all statuses".
  const [staffStatusFilter, setStaffStatusFilter] = useState<Array<'active' | 'sub' | 'inactive' | 'applicant' | 'offboarded'>>(['active']);
  const [hasCarFilter, setHasCarFilter] = useState<'any' | 'yes' | 'no'>('any');
  const [neighborhoodFilter, setNeighborhoodFilter] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState<string>('');

  // Right-side panel selection
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);

  const [teacherEventsById, setTeacherEventsById] = useState<Record<string, TeacherScheduleEvent[]>>({});
  const [loadingEventsFor, setLoadingEventsFor] = useState<string | null>(null);

  // Admin availability editor (weekly template) for a specific teacher/day
  const [isAvailEditorOpen, setIsAvailEditorOpen] = useState(false);
  const [availEditorTeacherId, setAvailEditorTeacherId] = useState<string | null>(null);
  const [availEditorDayKey, setAvailEditorDayKey] = useState<DayKey>('mon');
  const [availEditorDraft, setAvailEditorDraft] = useState<{
    status: AvailabilityStatus;
    start: string; // HH:mm in table timezone
    end: string;   // HH:mm in table timezone
    maybeReason: AvailabilityMaybeReason | null;
    maybeNote: string;
  }>({
    status: 'unavailable',
    start: '05:00',
    end: '23:00',
    maybeReason: null,
    maybeNote: '',
  });
  const [availEditorSaving, setAvailEditorSaving] = useState(false);
  const [availEditorError, setAvailEditorError] = useState<string | null>(null);

  const quarterDef = useMemo(() => QUARTERS.find(q => q.key === quarterKey) || QUARTERS[0], [quarterKey]);

  const anchorDate = quarterDef.startDate;

  // Week paging (Teachers view): Monday-first, mirrors the teacher-facing team page.
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeekMonday(new Date()));
  const viewingCurrentWeek = ymdLocal(weekStart) === ymdLocal(startOfWeekMonday(new Date()));

  const goToWeek = (next: Date) => {
    setWeekStart(next);
    // Keep the seasonal template in sync with the week being viewed.
    const key = ymdLocal(next);
    const q = QUARTERS.find(x => key >= x.startDate && key <= x.endDate);
    if (q && q.key !== quarterKey) setQuarterKey(q.key);
  };

  const weekInfoByDay = useMemo(() => {
    const map = {} as Record<DayKey, { date: Date; dateKey: string }>;
    DAYS.forEach((d, index) => {
      const date = addDaysLocal(weekStart, index);
      map[d.key] = { date, dateKey: ymdLocal(date) };
    });
    return map;
  }, [weekStart]);

  const weekLabel = `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${addDaysLocal(weekStart, 6).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  useEffect(() => {
    const run = async () => {
      setLoadingTeachers(true);
      setLoadingSquads(true);
      setLoadingAllEvents(true);
      try {
        const [teacherSnap, squadSnap, eventSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('role', '==', 'teacher'))),
          getDocs(collection(db, 'squads')),
          getDocs(collection(db, 'squadEvents')),
        ]);
        const teacherList = teacherSnap.docs.map(d => ({ ...(d.data() as any), id: d.id })) as User[];
        const squadList = squadSnap.docs.map(d => ({ ...(d.data() as any), id: d.id })) as Squad[];
        const eventsRaw = eventSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as any[];

        // Index events by teacherId (main + supporting)
        const perTeacher = new Map<string, Map<string, TeacherScheduleEvent>>();
        for (const e of eventsRaw) {
          const start = e.start?.toDate?.() || (e.start instanceof Date ? e.start : null);
          const end = e.end?.toDate?.() || (e.end instanceof Date ? e.end : null);
          if (!start || !end) continue;
          const base = {
            id: String(e.id),
            title: String(e.title || 'Class'),
            start,
            end,
            squadId: String(e.squadId || ''),
            subStatus: (e.subStatus === 'requested' || e.subStatus === 'assigned' || e.subStatus === 'canceled' ? e.subStatus : 'none') as TeacherScheduleEvent['subStatus'],
            subRequestReason: String(e.subRequestReason || ''),
            subRequestNote: String(e.subRequestNote || ''),
            subRequestedByTeacherId: String(e.subRequestedByTeacherId || ''),
            subAssignedTeacherId: String(e.subAssignedTeacherId || ''),
            subOriginalMainTeacherId: String(e.subOriginalMainTeacherId || ''),
            requestedRole: (e.activeCoverage?.[0]?.requestedRole === 'lead' || e.activeCoverage?.[0]?.requestedRole === 'co_teacher') ? e.activeCoverage[0].requestedRole : '',
            requestedTeacherId: String(e.activeCoverage?.[0]?.requestedTeacherId || ''),
          };
          const claimedCoverage = Array.isArray(e.activeCoverage)
            ? e.activeCoverage.find((coverage: any) => coverage?.status === 'claimed' && coverage?.substituteTeacherId)
            : null;

          // Lead teacher (main)
          if (typeof e.mainTeacherId === 'string' && e.mainTeacherId) {
            const tid = e.mainTeacherId;
            const m = perTeacher.get(tid) || new Map<string, TeacherScheduleEvent>();
            m.set(base.id, { ...base, role: claimedCoverage?.requestedRole === 'lead' && claimedCoverage.requestedTeacherId === tid ? 'sub_away' : 'lead' });
            perTeacher.set(tid, m);
          }

          // Original lead whose sub request was claimed: keep the class visible
          // (grayed out) on their row even though the claimer is now the main teacher.
          if (base.subStatus === 'assigned' && base.subOriginalMainTeacherId && base.subOriginalMainTeacherId !== String(e.mainTeacherId || '')) {
            const tid = base.subOriginalMainTeacherId;
            const m = perTeacher.get(tid) || new Map<string, TeacherScheduleEvent>();
            if (!m.has(base.id)) m.set(base.id, { ...base, role: 'sub_away' });
            perTeacher.set(tid, m);
          }

          // Supporting teacher(s)
          if (Array.isArray(e.supportingTeacherIds)) {
            e.supportingTeacherIds.forEach((tid: any) => {
              if (typeof tid !== 'string' || !tid) return;
              const m = perTeacher.get(tid) || new Map<string, TeacherScheduleEvent>();
              const existing = m.get(base.id);
              // If this teacher is also the lead teacher for the same event, keep the lead role.
              if (existing?.role === 'lead') {
                m.set(base.id, existing);
              } else {
                m.set(base.id, { ...base, role: claimedCoverage?.requestedRole === 'co_teacher' && claimedCoverage.requestedTeacherId === tid ? 'sub_away' : 'support' });
              }
              perTeacher.set(tid, m);
            });
          }
          if (claimedCoverage) {
            const tid = String(claimedCoverage.substituteTeacherId || '');
            if (tid) {
              const m = perTeacher.get(tid) || new Map<string, TeacherScheduleEvent>();
              m.set(base.id, { ...base, subAssignedTeacherId: tid, role: claimedCoverage.requestedRole === 'co_teacher' ? 'support' : 'lead' });
              perTeacher.set(tid, m);
            }
          }
        }
        const byTeacherId: Record<string, TeacherScheduleEvent[]> = {};
        perTeacher.forEach((m, tid) => {
          byTeacherId[tid] = Array.from(m.values()).sort((a, b) => a.start.getTime() - b.start.getTime());
        });

        setTeachers(teacherList);
        setSquads(squadList);
        setTeacherEventsById(byTeacherId);
      } finally {
        setLoadingTeachers(false);
        setLoadingSquads(false);
        setLoadingAllEvents(false);
      }
    };
    void run();
  }, []);

  // Events for every teacher in the currently viewed week, keyed by teacher then date.
  const weekEventsByTeacher = useMemo(() => {
    const dateKeys = new Set(Object.values(weekInfoByDay).map(info => info.dateKey));
    const out: Record<string, Record<string, TeacherScheduleEvent[]>> = {};
    Object.entries(teacherEventsById).forEach(([teacherId, events]) => {
      events.forEach(ev => {
        const dk = formatTimeInZone(ev.start, tableTz, 'yyyy-MM-dd');
        if (!dateKeys.has(dk)) return;
        ((out[teacherId] ||= {})[dk] ||= []).push(ev);
      });
    });
    return out;
  }, [teacherEventsById, weekInfoByDay, tableTz]);

  const teacherNameById = useMemo(() => {
    const map: Record<string, string> = {};
    teachers.forEach(t => { map[t.id] = String(t.displayName || t.email || t.id); });
    return map;
  }, [teachers]);

  const dayColumns = useMemo(() => {
    const base = DAYS.filter(d => showWeekends ? true : (d.key !== 'sat' && d.key !== 'sun'));
    return base;
  }, [showWeekends]);

  const timeRows = useMemo(() => {
    const s = minutes(gridStart);
    const e = minutes(gridEnd);
    const step = clamp(stepMinutes, 5, 180);
    if (!Number.isFinite(s) || !Number.isFinite(e) || !(s < e)) return [];
    const rows: Array<{ start: string; end: string; label: string }> = [];
    for (let m = s; m + step <= e; m += step) {
      const start = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
      const endM = m + step;
      const end = `${String(Math.floor(endM / 60)).padStart(2, '0')}:${String(endM % 60).padStart(2, '0')}`;
      rows.push({ start, end, label: `${fmtTimeInTz(start, tableTz, anchorDate)}–${fmtTimeInTz(end, tableTz, anchorDate)}` });
    }
    return rows;
  }, [gridStart, gridEnd, stepMinutes, tableTz, anchorDate]);

  const neighborhoods = useMemo(() => {
    const set = new Set<string>();
    teachers.forEach(t => {
      const ship = (t as any)?.shippingAddress || null;
      const city = String(ship?.city || '').trim();
      const state = String(ship?.state || '').trim();
      const derived = city && state ? `${city}, ${state}` : '';
      const n = (derived || (t as any).neighborhood || (t as any).address) as string | undefined;
      const v = (typeof n === 'string' ? n.trim() : '') || '';
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [teachers]);

  const states = useMemo(() => {
    const set = new Set<string>();
    teachers.forEach(t => {
      const shipState = String((t as any)?.shippingAddress?.state || '').trim();
      // NOTE: `??` can't be mixed with `||` without parens.
      const loc = shipState || ((t as any).neighborhood ?? (t as any).address);
      const st = inferStateFromLocationText(loc);
      if (st) set.add(st);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [teachers]);

  const teacherComputed = useMemo(() => {
    return teachers.map(t => {
      const template = normalizeTemplateForQuarter(t, quarterKey);
      const week = template?.week || null;
      const weeklyHours = computeWeeklyHours(week);
      const shipState = String((t as any)?.shippingAddress?.state || '').trim();
      // NOTE: `??` can't be mixed with `||` without parens.
      const loc = shipState || ((t as any).neighborhood ?? (t as any).address);
      const state = inferStateFromLocationText(loc);
      return { teacher: t, template, week, weeklyHours, state };
    });
  }, [teachers, quarterKey]);

  const normalizeStaffStatus = (v: unknown): 'active' | 'sub' | 'inactive' | 'applicant' | 'offboarded' | 'undefined' => {
    if (v === 'active' || v === 'sub' || v === 'inactive' || v === 'applicant' || v === 'offboarded') return v;
    return 'undefined';
  };

  const filteredTeachers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return teacherComputed
      .filter(({ teacher, weeklyHours, state }) => {
        const staffStatus = normalizeStaffStatus((teacher as any).staffStatus);
        if (staffStatusFilter.length > 0 && !staffStatusFilter.includes(staffStatus as any)) return false;
        if (hasCarFilter !== 'any') {
          const hasCar = teacher.hasCar === true;
          if (hasCarFilter === 'yes' && !hasCar) return false;
          if (hasCarFilter === 'no' && hasCar) return false;
        }
        if (stateFilter) {
          if ((state || '') !== stateFilter) return false;
        }
        if (neighborhoodFilter.length > 0) {
          const ship = (teacher as any)?.shippingAddress || null;
          const city = String(ship?.city || '').trim();
          const stateCode = String(ship?.state || '').trim();
          const derived = city && stateCode ? `${city}, ${stateCode}` : '';
          const n = (derived || (teacher as any).neighborhood || (teacher as any).address) as string | undefined;
          const nv = (typeof n === 'string' ? n.trim() : '') || '';
          if (!neighborhoodFilter.includes(nv)) return false;
        }
        if (q) {
          const name = (teacher.displayName || '').toLowerCase();
          const email = (teacher.email || '').toLowerCase();
          if (!name.includes(q) && !email.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (a.teacher.displayName || '').localeCompare(b.teacher.displayName || ''));
  }, [teacherComputed, search, staffStatusFilter, hasCarFilter, stateFilter, neighborhoodFilter]);

  const coverageStats = useMemo(() => {
    const missingTemplateCounts: Record<string, number> = {};
    const availableCounts: Record<string, number> = {};
    const maybeCounts: Record<string, number> = {};

    for (const d of dayColumns) {
      for (const row of timeRows) {
        const key = `${d.key}|${row.start}|${row.end}`;
        let missing = 0;
        let avail = 0;
        let maybe = 0;
        for (const { teacher, week } of filteredTeachers) {
          if (!week) {
            missing += 1;
            continue;
          }
          const day = getDayFromWeek(week, d.key);
          const st = (day?.status as AvailabilityStatus) || 'unavailable';
          if (st === 'unavailable') continue;

          const firstBlock = Array.isArray(day?.blocks) ? day.blocks[0] : null;
          const dayStartCT = typeof firstBlock?.start === 'string' ? firstBlock.start : null;
          const dayEndCT = typeof firstBlock?.end === 'string' ? firstBlock.end : null;
          const dayStart = dayStartCT ? convertTimeHHmm(dayStartCT, DEFAULT_TZ, tableTz, anchorDate) : null;
          const dayEnd = dayEndCT ? convertTimeHHmm(dayEndCT, DEFAULT_TZ, tableTz, anchorDate) : null;
          if (!dayStart || !dayEnd) continue;

          if (overlaps(dayStart, dayEnd, row.start, row.end)) {
            if (st === 'available') avail += 1;
            if (st === 'maybe') maybe += 1;
          }
        }
        missingTemplateCounts[key] = missing;
        availableCounts[key] = avail;
        maybeCounts[key] = maybe;
      }
    }

    const maxAvail = Math.max(1, ...Object.values(availableCounts));
    return { missingTemplateCounts, availableCounts, maybeCounts, maxAvail };
  }, [dayColumns, timeRows, filteredTeachers, tableTz, anchorDate]);

  const unassignedSquadsByDay = useMemo(() => {
    const map: Record<DayKey, Squad[]> = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
    const visible = new Set(dayColumns.map(d => d.key));
    squads.forEach(sq => {
      const st = (sq.status || 'published') as any;
      if (st === 'archived' || st === 'canceled') return;

      // Canonical lead teacher field in the app is `regularTeacher`.
      // Some older records may still use `leadTeacherId`.
      // IMPORTANT: `teacherIds` is NOT treated as "has a lead teacher" (it can contain non-lead assignments).
      const leadTeacherId = (sq as any).regularTeacher || (sq as any).leadTeacherId || null;
      if (typeof leadTeacherId === 'string' && leadTeacherId.trim()) return;

      const dk = normalizeDayOfWeekToKey(sq.schedule?.dayOfWeek);
      if (!dk) return;
      if (!visible.has(dk)) return;
      map[dk].push(sq);
    });
    (Object.keys(map) as DayKey[]).forEach(dk => {
      map[dk].sort((a, b) => {
        const aT = a.schedule?.startTime || '';
        const bT = b.schedule?.startTime || '';
        return aT.localeCompare(bT) || (a.title || '').localeCompare(b.title || '');
      });
    });
    return map;
  }, [squads, dayColumns]);

  const openSlot = (slot: Slot) => {
    setSelectedSlot(slot);
    setSelectedTeacherId(null);
    setPanelOpen(true);
  };

  const openTeacher = (teacherId: string) => {
    setSelectedTeacherId(teacherId);
    setPanelOpen(true);
  };

  const openTeacherDetail = (teacherId: string) => {
    navigate(`/app/admin/teachers/${teacherId}`, {
      state: {
        from: 'teacher-availability',
        backTo: '/app/admin/teacher-availability',
      },
    });
  };

  const openAvailabilityEditor = (teacherId: string, dayKey: DayKey) => {
    const t = teachers.find(x => x.id === teacherId) || null;
    if (!t) return;

    const tmpl = normalizeTemplateForQuarter(t, quarterKey);
    const weekCT = tmpl?.week || defaultWeekTemplateCT();
    const day = getDayFromWeek(weekCT, dayKey);
    const status: AvailabilityStatus = (day?.status as AvailabilityStatus) || 'unavailable';
    const firstBlock = Array.isArray(day?.blocks) ? day.blocks[0] : null;
    const startCT = typeof firstBlock?.start === 'string' ? firstBlock.start : defaultDayCT(dayKey).blocks[0].start;
    const endCT = typeof firstBlock?.end === 'string' ? firstBlock.end : defaultDayCT(dayKey).blocks[0].end;

    const start = convertTimeHHmm(startCT, DEFAULT_TZ, tableTz, anchorDate);
    const end = convertTimeHHmm(endCT, DEFAULT_TZ, tableTz, anchorDate);
    const maybeReason = (day?.maybeReason ?? null) as AvailabilityMaybeReason | null;
    const maybeNote = String(day?.maybeNote || '');

    setAvailEditorTeacherId(teacherId);
    setAvailEditorDayKey(dayKey);
    setAvailEditorDraft({
      status,
      start,
      end,
      maybeReason,
      maybeNote,
    });
    setAvailEditorError(null);
    setIsAvailEditorOpen(true);
  };

  const closeAvailabilityEditor = () => {
    setIsAvailEditorOpen(false);
    setAvailEditorTeacherId(null);
    setAvailEditorError(null);
  };

  const saveAvailabilityEditor = async () => {
    if (!availEditorTeacherId) return;
    const teacherId = availEditorTeacherId;
    const dayKey = availEditorDayKey;

    const { start, end } = normalizeTimeRange(availEditorDraft.start, availEditorDraft.end);
    if (!isValidTime(start) || !isValidTime(end)) {
      setAvailEditorError('Invalid time.');
      return;
    }
    if (!(minutes(start) < minutes(end))) {
      setAvailEditorError('Start must be before end.');
      return;
    }
    if (availEditorDraft.status === 'maybe' && !availEditorDraft.maybeReason) {
      setAvailEditorError('“Maybe” requires a reason.');
      return;
    }

    // Store in canonical CT on the teacher record.
    const startCT = convertTimeHHmm(start, tableTz, DEFAULT_TZ, anchorDate);
    const endCT = convertTimeHHmm(end, tableTz, DEFAULT_TZ, anchorDate);

    try {
      setAvailEditorSaving(true);
      setAvailEditorError(null);

      const t = teachers.find(x => x.id === teacherId) || null;
      if (!t) throw new Error('Teacher not found');

      const existing = normalizeTemplateForQuarter(t, quarterKey);
      const baseWeek = existing?.week ? clone(existing.week) : defaultWeekTemplateCT();

      const nextWeek = clone(baseWeek);
      const curDay = (nextWeek as any)[dayKey] || defaultDayCT(dayKey);
      (nextWeek as any)[dayKey] = {
        ...curDay,
        status: availEditorDraft.status,
        maybeReason: availEditorDraft.status === 'maybe' ? (availEditorDraft.maybeReason || null) : null,
        maybeNote: availEditorDraft.status === 'maybe' ? (availEditorDraft.maybeNote || '') : '',
        timeDetailsEnabled: true,
        blocks: [{ start: startCT, end: endCT, status: availEditorDraft.status }],
      };

      const payload: TeacherAvailabilityQuarterTemplate = {
        quarterKey,
        week: nextWeek,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, 'users', teacherId), {
        [`availabilityTemplates.${quarterKey}`]: payload,
      } as any);

      // Update local cache
      setTeachers(prev =>
        prev.map(u => {
          if (u.id !== teacherId) return u;
          const next: any = { ...u };
          next.availabilityTemplates = { ...(next.availabilityTemplates || {}), [quarterKey]: payload };
          return next;
        })
      );

      closeAvailabilityEditor();
    } catch (e: any) {
      console.error('Failed to save teacher availability (admin)', e);
      setAvailEditorError('Failed to save. Please try again.');
    } finally {
      setAvailEditorSaving(false);
    }
  };

  const selectedTeacher = useMemo(() => {
    if (!selectedTeacherId) return null;
    return teachers.find(t => t.id === selectedTeacherId) || null;
  }, [teachers, selectedTeacherId]);

  const selectedTeacherWeek = useMemo(() => {
    if (!selectedTeacher) return null;
    const t = normalizeTemplateForQuarter(selectedTeacher, quarterKey);
    return t?.week || null;
  }, [selectedTeacher, quarterKey]);

  const selectedSlotTeachers = useMemo(() => {
    if (!selectedSlot) return [];
    const dKey = selectedSlot.dayKey;
    const { start, end } = selectedSlot;
    const rows: Array<{ teacher: User; status: AvailabilityStatus | 'missing' }> = [];
    for (const { teacher, week } of filteredTeachers) {
      if (!week) {
        rows.push({ teacher, status: 'missing' });
        continue;
      }
      const day = getDayFromWeek(week, dKey);
      const st = (day?.status as AvailabilityStatus) || 'unavailable';
      if (st === 'unavailable') continue;
      const firstBlock = Array.isArray(day?.blocks) ? day.blocks[0] : null;
      const dsCT = typeof firstBlock?.start === 'string' ? firstBlock.start : null;
      const deCT = typeof firstBlock?.end === 'string' ? firstBlock.end : null;
      const ds = dsCT ? convertTimeHHmm(dsCT, DEFAULT_TZ, tableTz, anchorDate) : null;
      const de = deCT ? convertTimeHHmm(deCT, DEFAULT_TZ, tableTz, anchorDate) : null;
      if (!ds || !de) continue;
      if (!overlaps(ds, de, start, end)) continue;
      rows.push({ teacher, status: st });
    }
    rows.sort((a, b) => {
      const order = (s: any) => (s === 'available' ? 0 : s === 'maybe' ? 1 : s === 'missing' ? 2 : 3);
      return order(a.status) - order(b.status) || (a.teacher.displayName || '').localeCompare(b.teacher.displayName || '');
    });
    return rows;
  }, [selectedSlot, filteredTeachers, tableTz, anchorDate]);

  const loadTeacherEvents = async (teacherId: string) => {
    if (!teacherId) return;
    if (teacherEventsById[teacherId]) return;
    // If we've already loaded all events, don't fall back to per-teacher queries.
    if (loadingAllEvents === false) return;
    setLoadingEventsFor(teacherId);
    try {
      const byMain = await getDocs(query(collection(db, 'squadEvents'), where('mainTeacherId', '==', teacherId)));
      const bySupport = await getDocs(query(collection(db, 'squadEvents'), where('supportingTeacherIds', 'array-contains', teacherId)));
      const bySubstitute = await getDocs(query(collection(db, 'squadEvents'), where('subAssignedTeacherId', '==', teacherId)));
      const map = new Map<string, TeacherScheduleEvent>();
      const push = (snap: any, role: TeacherScheduleEvent['role']) => {
        snap.docs.forEach((docSnap: any) => {
          const data = docSnap.data();
          const start = data.start?.toDate?.() || new Date(0);
          const end = data.end?.toDate?.() || new Date(0);
          const existing = map.get(docSnap.id);
          // If we already have a lead role recorded, keep it.
          if (existing?.role === 'lead') return;
          map.set(docSnap.id, {
            id: docSnap.id,
            title: String(data.title || 'Class'),
            start,
            end,
            squadId: String(data.squadId || ''),
            role,
            subStatus: (data.subStatus === 'requested' || data.subStatus === 'assigned' || data.subStatus === 'canceled' ? data.subStatus : 'none') as TeacherScheduleEvent['subStatus'],
            subRequestReason: String(data.subRequestReason || ''),
            subRequestNote: String(data.subRequestNote || ''),
            subRequestedByTeacherId: String(data.subRequestedByTeacherId || ''),
            subAssignedTeacherId: String(data.subAssignedTeacherId || ''),
            subOriginalMainTeacherId: String(data.subOriginalMainTeacherId || ''),
            requestedRole: (data.activeCoverage?.[0]?.requestedRole === 'lead' || data.activeCoverage?.[0]?.requestedRole === 'co_teacher') ? data.activeCoverage[0].requestedRole : '',
            requestedTeacherId: String(data.activeCoverage?.[0]?.requestedTeacherId || ''),
          });
        });
      };
      push(bySupport, 'support');
      push(byMain, 'lead');
      push(bySubstitute, 'lead');
      setTeacherEventsById(prev => ({ ...prev, [teacherId]: Array.from(map.values()) }));
    } finally {
      setLoadingEventsFor(null);
    }
  };

  // When a teacher is selected, ensure events are available for conflicts overlay
  useEffect(() => {
    if (selectedTeacherId) {
      void loadTeacherEvents(selectedTeacherId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeacherId]);

  const selectedTeacherQuarterExceptions = useMemo(() => {
    if (!selectedTeacher) return [];
    const raw = ((selectedTeacher as any)?.availabilityExceptions?.[quarterKey] || {}) as Record<string, any>;
    const start = quarterDef.startDate;
    const end = quarterDef.endDate;
    const keys = Object.keys(raw || {}).filter(k => k >= start && k <= end).sort();
    return keys.map(dateKey => ({ dateKey, raw: raw[dateKey] }));
  }, [selectedTeacher, quarterKey, quarterDef.startDate, quarterDef.endDate]);

  const selectedTeacherEventsInQuarter = useMemo(() => {
    if (!selectedTeacherId) return [];
    const all = teacherEventsById[selectedTeacherId] || [];
    const start = createDateInZone(quarterDef.startDate, '00:00', DEFAULT_TZ).getTime();
    const end = createDateInZone(quarterDef.endDate, '23:59', DEFAULT_TZ).getTime();
    return all
      .filter(ev => ev.start.getTime() >= start && ev.start.getTime() <= end)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [selectedTeacherId, teacherEventsById, quarterDef.startDate, quarterDef.endDate]);

  const selectedTeacherClassSegmentsByDay = useMemo(() => {
    const gs = minutes(gridStart);
    const ge = minutes(gridEnd);
    const span = Math.max(1, ge - gs);
    const map: Record<DayKey, Array<{ leftPct: number; widthPct: number; label: string; eventId: string }>> = {
      mon: [],
      tue: [],
      wed: [],
      thu: [],
      fri: [],
      sat: [],
      sun: [],
    };
    if (!Number.isFinite(gs) || !Number.isFinite(ge) || !(gs < ge)) return map;
    for (const ev of selectedTeacherEventsInQuarter) {
      const dk = (() => {
        const dow = formatTimeInZone(ev.start, tableTz, 'EEE').toLowerCase();
        const m: Record<string, DayKey> = { mon: 'mon', tue: 'tue', wed: 'wed', thu: 'thu', fri: 'fri', sat: 'sat', sun: 'sun' };
        return m[dow] || 'mon';
      })();
      const s = minutes(formatTimeInZone(ev.start, tableTz, 'HH:mm'));
      const e = minutes(formatTimeInZone(ev.end, tableTz, 'HH:mm'));
      if (!Number.isFinite(s) || !Number.isFinite(e) || !(s < e)) continue;
      const left = clamp(((s - gs) / span) * 100, 0, 100);
      const right = clamp(((e - gs) / span) * 100, 0, 100);
      const width = clamp(right - left, 0, 100);
      if (width <= 0) continue;
      map[dk].push({
        leftPct: left,
        widthPct: width,
        label: `${formatTimeInZone(ev.start, tableTz, 'h:mm a')}–${formatTimeInZone(ev.end, tableTz, 'h:mm a')} · ${ev.title}`,
        eventId: ev.id,
      });
    }
    // Stable ordering within each day
    (Object.keys(map) as DayKey[]).forEach(dk => {
      map[dk].sort((a, b) => a.leftPct - b.leftPct || a.widthPct - b.widthPct);
    });
    return map;
  }, [selectedTeacherEventsInQuarter, gridStart, gridEnd, tableTz]);

  const onBack = () => {
    navigate('/app/admin', { state: { adminTab: 'users', adminUserTab: 'teachers' } as any });
  };

  if (loadingTeachers) {
    return (
      <div className="ogta-root">
        <div className="ogta-top">
          <button type="button" className="ogta-back" onClick={onBack}>← Back to Teachers</button>
          <div className="ogta-title">Teacher Availability</div>
        </div>
        <div className="admin-content-card">Loading teachers…</div>
      </div>
    );
  }

  return (
    <div className={`ogta-root ${panelOpen ? 'is-panel-open' : ''}`}>
      <div className="ogta-top">
        <div className="ogta-top-left">
          <button type="button" className="ogta-back" onClick={onBack}>← Back to Teachers</button>
        </div>

        <div className="ogta-top-center">
          <div className="ogta-title-row">
            <div className="ogta-title">
              Teacher Availability
            </div>
            <select
              className="admin-select ogta-select ogta-select--inline"
              value={quarterKey}
              onChange={(e) => {
                const nextKey = e.target.value as AvailabilityQuarterKey;
                setQuarterKey(nextKey);
                setSelectedSlot(null);
                setSelectedTeacherId(null);
                // Jump the week view into the selected season.
                const q = QUARTERS.find(x => x.key === nextKey);
                if (q) {
                  const todayKey = ymdLocal(new Date());
                  setWeekStart(todayKey >= q.startDate && todayKey <= q.endDate
                    ? startOfWeekMonday(new Date())
                    : startOfWeekMonday(new Date(`${q.startDate}T12:00:00`)));
                }
              }}
              aria-label="Season"
              title="Season"
            >
              {QUARTERS.map(q => (
                <option key={q.key} value={q.key}>{q.label}</option>
              ))}
            </select>
            <select
              className="admin-select ogta-tz"
              value={tableTz}
              onChange={(e) => {
                const next = e.target.value as TableTz;
                // Convert the existing window so it represents the same moments in time.
                setGridStart(prev => convertTimeHHmm(prev, tableTz, next, anchorDate));
                setGridEnd(prev => convertTimeHHmm(prev, tableTz, next, anchorDate));
                setTableTz(next);
              }}
              aria-label="Table time zone"
              title="Table time zone"
            >
              {TABLE_TZS.map(z => (
                <option key={z.tz} value={z.tz}>{z.label}</option>
              ))}
            </select>
          </div>

          <div className="ogta-controls">
            <label className="ogta-ctrl">
              <span className="ogta-ctrl-label">Grid</span>
              <span className="ogta-ctrl-row">
                <input
                  className="admin-input ogta-time"
                  type="time"
                  value={gridStart}
                  onChange={(e) => setGridStart(e.target.value)}
                  aria-label="Grid start time"
                />
                <span className="ogta-ctrl-sep">–</span>
                <input
                  className="admin-input ogta-time"
                  type="time"
                  value={gridEnd}
                  onChange={(e) => setGridEnd(e.target.value)}
                  aria-label="Grid end time"
                />
                <select
                  className="admin-select ogta-step"
                  value={String(stepMinutes)}
                  onChange={(e) => setStepMinutes(Number(e.target.value))}
                  aria-label="Time block interval"
                >
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="60">60 min</option>
                </select>
                <label className="ogta-weekends">
                  <input
                    type="checkbox"
                    checked={showWeekends}
                    onChange={(e) => setShowWeekends(e.target.checked)}
                  />
                  <span>Weekends</span>
                </label>
              </span>
            </label>
          </div>
        </div>

        <div className="ogta-top-right">
          <div className="ogta-view-toggle" role="tablist" aria-label="View mode">
            <button
              type="button"
              className={`ogta-toggle ${view === 'coverage' ? 'is-active' : ''}`}
              onClick={() => setView('coverage')}
              role="tab"
              aria-selected={view === 'coverage'}
            >
              Coverage
            </button>
            <button
              type="button"
              className={`ogta-toggle ${view === 'teachers' ? 'is-active' : ''}`}
              onClick={() => setView('teachers')}
              role="tab"
              aria-selected={view === 'teachers'}
            >
              Teachers
            </button>
          </div>

          <input
            className="admin-input ogta-search"
            placeholder="Search teachers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search teachers"
          />

          <button
            type="button"
            className="ogta-filters-btn"
            onClick={() => setFiltersOpen(v => !v)}
            aria-expanded={filtersOpen}
          >
            Filters
          </button>
        </div>

        {filtersOpen && (
          <div className="ogta-filters-row">
            <div className="ogta-filter">
              <span className="ogta-filter-label">Staff status (any checked; none = all)</span>
              <div className="ogta-filter-checks">
                {([
                  ['active', 'Active'],
                  ['sub', 'Sub'],
                  ['inactive', 'Inactive'],
                  ['applicant', 'Applicant'],
                  ['offboarded', 'Offboarded'],
                ] as const).map(([value, label]) => (
                  <label key={value} className="ogta-filter-check">
                    <input
                      type="checkbox"
                      checked={staffStatusFilter.includes(value)}
                      onChange={(e) => {
                        setStaffStatusFilter(prev => e.target.checked
                          ? [...prev, value]
                          : prev.filter(v => v !== value));
                      }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <label className="ogta-filter">
              <span className="ogta-filter-label">Has car</span>
              <select
                className="admin-select"
                value={hasCarFilter}
                onChange={(e) => setHasCarFilter(e.target.value as any)}
              >
                <option value="any">Any</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>

            <label className="ogta-filter">
              <span className="ogta-filter-label">State</span>
              <select
                className="admin-select"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <option value="">All</option>
                {states.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </label>

            <label className="ogta-filter">
              <span className="ogta-filter-label">Neighborhood</span>
              <select
                className="admin-select"
                value={neighborhoodFilter[0] || ''}
                onChange={(e) => setNeighborhoodFilter(e.target.value ? [e.target.value] : [])}
              >
                <option value="">All</option>
                {neighborhoods.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="ogta-main">
        <div className="ogta-workspace">
          {view === 'coverage' ? (
            <div className="ogta-coverage">
              {timeRows.length === 0 ? (
                <div className="admin-content-card">
                  Please pick a valid grid time window (start must be before end).
                </div>
              ) : (
                <>
                  <div
                    className="ogta-coverage-grid"
                    role="table"
                    aria-label="Coverage grid"
                    style={{ ['--ogta-dayCols' as any]: String(dayColumns.length) }}
                  >
                    <div className="ogta-coverage-head" role="row">
                      <div className="ogta-coverage-corner" role="columnheader">Time</div>
                      {dayColumns.map(d => (
                        <div key={d.key} className="ogta-coverage-day" role="columnheader">{d.label}</div>
                      ))}
                    </div>

                    {timeRows.map(row => (
                      <div key={row.start} className="ogta-coverage-row" role="row">
                        <div className="ogta-coverage-time" role="rowheader">{row.label}</div>
                        {dayColumns.map(d => {
                          const k = `${d.key}|${row.start}|${row.end}`;
                          const avail = coverageStats.availableCounts[k] || 0;
                          const maybe = coverageStats.maybeCounts[k] || 0;
                          const missing = coverageStats.missingTemplateCounts[k] || 0;
                          const intensity = avail / coverageStats.maxAvail;
                          // Keep cells visually "white", but add subtle neutral ink shading for coverage strength.
                          const shade = 0.02 + 0.10 * intensity; // 0.02..0.12
                          const border = 0.08 + 0.22 * intensity; // 0.08..0.30
                          return (
                            <button
                              key={k}
                              type="button"
                              className="ogta-coverage-cell"
                              style={{
                                background: '#ffffff',
                                boxShadow: `inset 0 0 0 999px rgba(15, 23, 42, ${shade})`,
                                borderLeft: `4px solid rgba(15, 23, 42, ${border})`,
                              }}
                              onClick={() => openSlot({ dayKey: d.key, start: row.start, end: row.end })}
                              aria-label={`${d.label} ${row.label}: ${avail} available, ${maybe} maybe${missing ? `, ${missing} missing template` : ''}`}
                            >
                              <div className="ogta-cell-main">{avail}</div>
                              <div className="ogta-cell-sub">
                                <span className="ogta-maybe">maybe {maybe}</span>
                                {missing > 0 && <span className="ogta-missing">missing {missing}</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  <div className="ogta-unassigned">
                    <div className="ogta-unassigned-head">
                      <div className="ogta-unassigned-title">Unassigned squads (no lead teacher)</div>
                      {loadingSquads && <div className="ogta-unassigned-sub">Loading squads…</div>}
                    </div>

                    <div
                      className="ogta-unassigned-grid"
                      style={{ ['--ogta-dayCols' as any]: String(dayColumns.length) }}
                    >
                    <div className="ogta-unassigned-spacer" aria-hidden="true" />
                      {dayColumns.map(d => (
                        <div key={d.key} className="ogta-unassigned-col">
                          <div className="ogta-unassigned-colhead">{d.label}</div>
                          <div className="ogta-unassigned-list">
                            {(unassignedSquadsByDay[d.key] || []).length === 0 ? (
                              <div className="ogta-unassigned-empty">—</div>
                            ) : (
                              unassignedSquadsByDay[d.key].map(sq => {
                                const fromTz = (sq.timezone || DEFAULT_TZ) as string;
                                const st = sq.schedule?.startTime ? convertTimeHHmm(sq.schedule.startTime, fromTz, tableTz, anchorDate) : '';
                                const en = sq.schedule?.endTime ? convertTimeHHmm(sq.schedule.endTime, fromTz, tableTz, anchorDate) : '';
                                const timeLabel = (st && en)
                                  ? `${formatTimeInZone(createDateInZone(anchorDate, st, tableTz), tableTz, 'h:mm a')}–${formatTimeInZone(createDateInZone(anchorDate, en, tableTz), tableTz, 'h:mm a')} ${tzLabel(tableTz)}`
                                  : '';
                                const loc = sq.locationName || sq.schoolName || sq.address || '';
                                return (
                                  <div key={sq.id} className="ogta-unassigned-item">
                                    <div className="ogta-unassigned-item-title">{sq.title || 'Untitled squad'}</div>
                                    {(timeLabel || loc) && (
                                      <div className="ogta-unassigned-item-sub">
                                        {timeLabel && <span>{timeLabel}</span>}
                                        {timeLabel && loc && <span className="ogta-dot">·</span>}
                                        {loc && <span className="ogta-unassigned-loc">{String(loc)}</span>}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="ogta-teachers">
              <div className="ogta-week-nav">
                <button type="button" aria-label="Previous week" onClick={() => goToWeek(addDaysLocal(weekStart, -7))}>‹</button>
                <div className="ogta-week-nav-label">
                  <strong>{viewingCurrentWeek ? 'This week' : 'Week'}</strong>
                  <span>{weekLabel}</span>
                </div>
                {!viewingCurrentWeek
                  ? <button type="button" className="ogta-week-today" onClick={() => goToWeek(startOfWeekMonday(new Date()))}>This week</button>
                  : <span />}
                <button type="button" aria-label="Next week" onClick={() => goToWeek(addDaysLocal(weekStart, 7))}>›</button>
              </div>

              <div className="ogta-legend" aria-label="Availability legend">
                <span><i className="ogta-legend-dot is-available" /> Available</span>
                <span><i className="ogta-legend-dot is-maybe" /> Maybe</span>
                <span><i className="ogta-legend-dot is-committed" /> Committed as lead</span>
                <span><i className="ogta-legend-dot is-subrequested" /> Sub requested (unfilled)</span>
                <span><i className="ogta-legend-dot is-subcovered" /> Covered by a sub</span>
                <span><i className="ogta-legend-dot is-unavailable" /> Unavailable</span>
                <span><i className="ogta-legend-dot is-notset" /> Not set</span>
              </div>

              <div className="ogta-card-list" aria-label="Teachers availability">
                {filteredTeachers.map(({ teacher, week }) => (
                  <article className="ogta-card" key={teacher.id}>
                    <div className="ogta-card-identity">
                      {(teacher as any).headshotUrl
                        ? <img className="ogta-card-avatar ogta-card-avatar--photo" src={(teacher as any).headshotUrl} alt={`${teacher.displayName || 'Teacher'} profile`} />
                        : <span className="ogta-card-avatar">{String(teacher.displayName || teacher.email || 'T').slice(0, 1).toUpperCase()}</span>}
                      <div>
                        <button
                          type="button"
                          className="ogta-teacher-btn"
                          onClick={() => openTeacherDetail(teacher.id)}
                        >
                          <div className="ogta-teacher-name">{teacher.displayName || teacher.email || teacher.id}</div>
                        </button>
                        <div className="ogta-teacher-meta">
                          {(() => {
                            const st = normalizeStaffStatus((teacher as any).staffStatus);
                            return (
                              <span className={`ogta-badge ${(st === 'active') ? 'is-active' : 'is-muted'}`}>
                                {st}
                              </span>
                            );
                          })()}
                          {teacher.hasCar === true && <span className="ogta-badge is-blue">has car</span>}
                          {teacher.hasCar === false && <span className="ogta-badge is-red">no car</span>}
                          {((teacher as any).neighborhood ?? (teacher as any).address) && (
                            <span className="ogta-badge is-muted">{String((teacher as any).neighborhood ?? (teacher as any).address)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="ogta-card-days" style={{ ['--ogta-cardCols' as any]: String(dayColumns.length) }}>
                      {dayColumns.map(d => {
                        const info = weekInfoByDay[d.key];
                        const dateKey = info.dateKey;
                        const exceptionRaw = (teacher as any)?.availabilityExceptions?.[quarterKey]?.[dateKey];
                        const dayEvents = weekEventsByTeacher[teacher.id]?.[dateKey] || [];
                        const leadEvents = dayEvents.filter(ev =>
                          ev.role === 'lead'
                          || ev.role === 'sub_away'
                          // Substitute covering this teacher's claim — either role —
                          // must show as busy on THEIR row too.
                          || (ev.subStatus === 'assigned' && ev.subAssignedTeacherId === teacher.id)
                          // This teacher's own open co-teacher coverage request.
                          || (ev.subStatus === 'requested' && ev.requestedRole === 'co_teacher' && ev.requestedTeacherId === teacher.id));

                        const base = week ? getDayRange(week, d.key) : null;
                        const excStatus = exceptionRaw ? exceptionStatus(exceptionRaw) : null;
                        const excRange = exceptionRaw ? exceptionRange(exceptionRaw) : {};
                        const hasAvailability = Boolean(week || exceptionRaw);
                        const status: string = hasAvailability ? (excStatus ?? base?.status ?? 'unavailable') : 'not_set';
                        const startCT = excRange.start || base?.start || '00:00';
                        const endCT = excRange.end || base?.end || '00:00';
                        const maybeReason = (exceptionRaw
                          ? (exceptionRaw?.maybeReason ?? null)
                          : base?.maybeReason ?? null) as AvailabilityMaybeReason | null;
                        const maybeNote = exceptionRaw
                          ? String(exceptionRaw?.maybeNote || exceptionRaw?.note || '')
                          : String(base?.maybeNote || '');

                        const start = convertTimeHHmm(startCT, DEFAULT_TZ, tableTz, anchorDate);
                        const end = convertTimeHHmm(endCT, DEFAULT_TZ, tableTz, anchorDate);
                        const timesLabel = `${fmtTimeInTz(start, tableTz, anchorDate)}–${fmtTimeInTz(end, tableTz, anchorDate)}`;
                        const detail = status === 'available' || status === 'maybe'
                          ? timesLabel
                          : status === 'unavailable' ? 'Unavailable' : 'Not set';
                        const hasLead = leadEvents.some(ev =>
                          (ev.role === 'lead' && ev.subStatus !== 'requested')
                          || (ev.subStatus === 'assigned' && ev.subAssignedTeacherId === teacher.id));

                        return (
                          <div
                            key={d.key}
                            className={`ogta-day ogta-day--${status}${hasLead ? ' ogta-day--committed' : ''}`}
                            onClick={() => openTeacher(teacher.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e2) => {
                              if (e2.key === 'Enter' || e2.key === ' ') openTeacher(teacher.id);
                            }}
                          >
                            <div className="ogta-day-heading">
                              <b>{d.label}</b>
                              <span className="ogta-day-heading-right">
                                {exceptionRaw && <i className="ogta-exc-dot" title="Date-specific override for this day" />}
                                <time>{info.date.getDate()}</time>
                              </span>
                            </div>
                            <span className="ogta-day-detail">{detail}</span>
                            {status === 'maybe' && maybeTooltip(maybeReason, maybeNote)}
                            {leadEvents.map(ev => {
                              const timeRange = `${formatTimeInZone(ev.start, tableTz, 'h:mm a')}–${formatTimeInZone(ev.end, tableTz, 'h:mm a')}`;
                              if (ev.role === 'sub_away') {
                                const claimer = teacherNameById[ev.subAssignedTeacherId] || 'another teacher';
                                return (
                                  <div key={ev.id} className="ogta-day-class is-sub-covered" onClick={e2 => e2.stopPropagation()}>
                                    <b>Sub covering · {formatTimeInZone(ev.start, tableTz, 'h:mm a')}</b>
                                    <span>{ev.title}</span>
                                    <small>Claimed by {claimer}</small>
                                    {subTooltip('Sub requested — claimed', ev.subRequestReason, ev.subRequestNote, 'Claimed by', claimer)}
                                  </div>
                                );
                              }
                              if (ev.subStatus === 'requested') {
                                return (
                                  <div key={ev.id} className="ogta-day-class is-sub-open" onClick={e2 => e2.stopPropagation()}>
                                    <b>Sub requested · {formatTimeInZone(ev.start, tableTz, 'h:mm a')}</b>
                                    <span>{ev.title}</span>
                                    <small>Not claimed yet</small>
                                    {subTooltip('Sub requested — unfilled', ev.subRequestReason, ev.subRequestNote)}
                                  </div>
                                );
                              }
                              if (ev.subStatus === 'assigned' && ev.subAssignedTeacherId === teacher.id) {
                                const originalId = ev.requestedTeacherId || ev.subOriginalMainTeacherId;
                                const original = teacherNameById[originalId] || (ev.requestedRole === 'co_teacher' ? 'the original co-teacher' : 'the original lead');
                                const coverageRole = ev.requestedRole === 'co_teacher' ? 'co-teacher' : 'lead';
                                return (
                                  <div key={ev.id} className="ogta-day-class is-sub-cover" onClick={e2 => e2.stopPropagation()}>
                                    <b>Sub cover · {formatTimeInZone(ev.start, tableTz, 'h:mm a')}</b>
                                    <span>{ev.title}</span>
                                    <small>Covering {coverageRole} for {original}</small>
                                    {subTooltip(`Covering as ${coverageRole} sub`, ev.subRequestReason, ev.subRequestNote, 'Covering for', original)}
                                  </div>
                                );
                              }
                              return (
                                <div key={ev.id} className="ogta-day-class" title={`${ev.title} · ${timeRange} ${tzLabel(tableTz)}`}>
                                  <b>Lead · {formatTimeInZone(ev.start, tableTz, 'h:mm a')}</b>
                                  <span>{ev.title}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className={`ogta-panel ${panelOpen ? 'is-open' : ''}`} aria-label="Teacher availability detail panel">
          <div className="ogta-panel-inner">
            <div className="ogta-panel-head">
              <div className="ogta-panel-title">
                {selectedSlot ? (
                  <>
                    <div className="ogta-panel-kicker">Slot</div>
                    <div className="ogta-panel-h1">
                      {DAYS.find(d => d.key === selectedSlot.dayKey)?.label} {fmtTimeInTz(selectedSlot.start, tableTz, anchorDate)}–{fmtTimeInTz(selectedSlot.end, tableTz, anchorDate)} {tzLabel(tableTz)}
                    </div>
                  </>
                ) : selectedTeacher ? (
                  <>
                    <div className="ogta-panel-kicker">Teacher</div>
                    <div className="ogta-panel-h1">{selectedTeacher.displayName || selectedTeacher.email || selectedTeacher.id}</div>
                  </>
                ) : (
                  <div className="ogta-panel-h1">Details</div>
                )}
              </div>

              <button type="button" className="ogta-panel-close" onClick={() => { setPanelOpen(false); setSelectedSlot(null); }}>
                Close
              </button>
            </div>

            {selectedSlot && (
              <div className="ogta-panel-section">
                <div className="ogta-section-title">Teachers covering this slot</div>
                <div className="ogta-slot-list">
                  {selectedSlotTeachers.length === 0 ? (
                    <div className="ogta-empty">No teachers match this slot with current filters.</div>
                  ) : (
                    selectedSlotTeachers.map(({ teacher, status }) => (
                      <button
                        key={teacher.id}
                        type="button"
                        className="ogta-slot-teacher"
                        onClick={() => openTeacherDetail(teacher.id)}
                      >
                        <div className="ogta-slot-teacher-name">{teacher.displayName || teacher.email || teacher.id}</div>
                        <div className="ogta-slot-teacher-badges">
                          <span className={`ogta-pill ${status === 'available' ? 'is-available' : status === 'maybe' ? 'is-maybe' : 'is-missing'}`}>
                            {status}
                          </span>
                          {teacher.hasCar && <span className="ogta-pill is-blue">has car</span>}
                          {((teacher as any).neighborhood ?? (teacher as any).address) && (
                            <span className="ogta-pill is-muted">{String((teacher as any).neighborhood ?? (teacher as any).address)}</span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {selectedTeacher && (
              <>
                <div className="ogta-panel-section">
                  <div className="ogta-section-title" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <span>Profile</span>
                    <button
                      type="button"
                      className="ogta-linkbtn"
                      onClick={() => openTeacherDetail(selectedTeacher.id)}
                    >
                      Open full profile
                    </button>
                  </div>
                  <div className="ogta-badges-row">
                    {(() => {
                      const st = normalizeStaffStatus((selectedTeacher as any).staffStatus);
                      return (
                        <span className={`ogta-pill ${(st === 'active') ? 'is-available' : 'is-muted'}`}>
                          {st}
                        </span>
                      );
                    })()}
                    {selectedTeacher.hasCar === true && <span className="ogta-pill is-blue">has car</span>}
                    {selectedTeacher.hasCar === false && <span className="ogta-pill is-red">no car</span>}
                    {((selectedTeacher as any).neighborhood ?? (selectedTeacher as any).address) && (
                      <span className="ogta-pill is-muted">{String((selectedTeacher as any).neighborhood ?? (selectedTeacher as any).address)}</span>
                    )}
                    {selectedTeacher.timeZone && <span className="ogta-pill is-muted">{selectedTeacher.timeZone}</span>}
                  </div>
                </div>

                <div className="ogta-panel-section">
                  <div className="ogta-section-title">Weekly availability (season)</div>
                  {!selectedTeacherWeek ? (
                    <div className="ogta-empty">
                      No weekly template saved for this season.{' '}
                      <button
                        type="button"
                        className="ogta-linkbtn"
                        onClick={() => openAvailabilityEditor(selectedTeacher.id, dayColumns[0]?.key || 'mon')}
                      >
                        Set availability
                      </button>
                    </div>
                  ) : (
                    <div className="ogta-week-mini">
                      {dayColumns.map(d => {
                        const r = getDayRange(selectedTeacherWeek, d.key);
                        const start = convertTimeHHmm(r.start, DEFAULT_TZ, tableTz, anchorDate);
                        const end = convertTimeHHmm(r.end, DEFAULT_TZ, tableTz, anchorDate);
                        return (
                          <div
                            key={d.key}
                            className="ogta-week-mini-row ogta-week-mini-row--clickable"
                            onClick={() => openAvailabilityEditor(selectedTeacher.id, d.key)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') openAvailabilityEditor(selectedTeacher.id, d.key);
                            }}
                            title="Click to edit availability"
                          >
                            <div className="ogta-week-mini-day">{d.label}</div>
                            {r.status === 'maybe' ? (
                              <span className="ogta-tooltip-wrap">
                                <span className={`ogta-week-mini-pill is-maybe`}>maybe</span>
                                {maybeTooltip(r.maybeReason, r.maybeNote)}
                              </span>
                            ) : (
                              <div className={`ogta-week-mini-pill ${r.status === 'available' ? 'is-available' : 'is-unavailable'}`}>
                                {r.status}
                              </div>
                            )}
                            <div className="ogta-week-mini-time">
                              {fmtTimeInTz(start, tableTz, anchorDate)}–{fmtTimeInTz(end, tableTz, anchorDate)} {tzLabel(tableTz)}
                              <div className="ogta-week-mini-barwrap" aria-label={`${d.label} availability bar`}>
                                <div className={`ogta-bar ogta-bar--mini ${r.status === 'available' ? 'is-available' : r.status === 'maybe' ? 'is-maybe' : 'is-unavailable'}`}>
                                  {r.status !== 'unavailable' && (() => {
                                    const gs = minutes(gridStart);
                                    const ge = minutes(gridEnd);
                                    const span = Math.max(1, ge - gs);
                                    const start = convertTimeHHmm(r.start, DEFAULT_TZ, tableTz, anchorDate);
                                    const end = convertTimeHHmm(r.end, DEFAULT_TZ, tableTz, anchorDate);
                                    const s = minutes(start);
                                    const e = minutes(end);
                                    const left = Number.isFinite(s) ? clamp(((s - gs) / span) * 100, 0, 100) : 0;
                                    const right = Number.isFinite(e) ? clamp(((e - gs) / span) * 100, 0, 100) : 0;
                                    const width = clamp(right - left, 0, 100);
                                    return (
                                      <span className="ogta-bar-fill" style={{ left: `${left}%`, width: `${width}%` }} />
                                    );
                                  })()}
                                  {selectedTeacherClassSegmentsByDay[d.key]?.map(seg => (
                                    <span
                                      key={seg.eventId}
                                      className="ogta-class-seg"
                                      style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%` }}
                                      title={seg.label}
                                      aria-label={`Class: ${seg.label}`}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="ogta-panel-section">
                  <div className="ogta-section-title">Seasonal date exceptions</div>
                  {selectedTeacherQuarterExceptions.length === 0 ? (
                    <div className="ogta-empty">No exceptions saved in this season.</div>
                  ) : (
                    <div className="ogta-exceptions">
                      {selectedTeacherQuarterExceptions.map(({ dateKey, raw }) => {
                        const st = exceptionStatus(raw) || 'unavailable';
                        const r = exceptionRange(raw);
                        const label = `${dateKey}`;
                        const timeLabel = r.start && r.end ? `${fmtTime(r.start)}–${fmtTime(r.end)} CT` : '';
                        const note = String(raw?.maybeNote || raw?.note || '').trim();
                        const reason = String(raw?.maybeReason || '').trim();
                        return (
                          <div key={dateKey} className="ogta-exception-row">
                            <div className="ogta-exc-date">{label}</div>
                            <span className={`ogta-pill ${st === 'available' ? 'is-available' : st === 'maybe' ? 'is-maybe' : 'is-unavailable'}`}>
                              {st}
                            </span>
                            <div className="ogta-exc-time">{timeLabel}</div>
                            {(reason || note) && (
                              <div className="ogta-exc-note">
                                {reason && <span className="ogta-exc-reason">{reason}</span>}
                                {note && <span className="ogta-exc-note-text">{note}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="ogta-panel-section">
                  <div className="ogta-section-title">Assigned classes (season)</div>
                  {loadingEventsFor === selectedTeacher.id && <div className="ogta-empty">Loading classes…</div>}
                  {loadingEventsFor !== selectedTeacher.id && selectedTeacherEventsInQuarter.length === 0 && (
                    <div className="ogta-empty">No classes found for this season.</div>
                  )}
                  {selectedTeacherEventsInQuarter.length > 0 && (
                    <div className="ogta-events">
                      {selectedTeacherEventsInQuarter.map(ev => {
                        const dateKey = formatTimeInZone(ev.start, tableTz, 'yyyy-MM-dd');
                        const tLabel = `${formatTimeInZone(ev.start, tableTz, 'EEE, MMM d')} · ${formatTimeInZone(ev.start, tableTz, 'h:mm a')}–${formatTimeInZone(ev.end, tableTz, 'h:mm a')} ${tzLabel(tableTz)}`;
                        return (
                          <div key={ev.id} className="ogta-event-row">
                            <div className="ogta-event-title">{ev.title}</div>
                            <div className="ogta-event-sub">{tLabel}</div>
                            <div className="ogta-event-sub ogta-event-sub-muted">{dateKey}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {!selectedSlot && !selectedTeacher && (
              <div className="ogta-panel-section">
                <div className="ogta-empty">Click a coverage cell or a teacher to see details.</div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {isAvailEditorOpen && availEditorTeacherId && (
        <div className="ogta-modal-overlay" onClick={closeAvailabilityEditor}>
          <div className="ogta-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ogta-modal-head">
              <div className="ogta-modal-title">
                Edit availability — {DAYS.find(d => d.key === availEditorDayKey)?.label}
              </div>
              <button type="button" className="ogta-modal-close" onClick={closeAvailabilityEditor} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="ogta-modal-sub">
              Teacher: <b>{(teachers.find(t => t.id === availEditorTeacherId)?.displayName) || (teachers.find(t => t.id === availEditorTeacherId)?.email) || availEditorTeacherId}</b>{' '}
              · Season: <b>{QUARTERS.find(q => q.key === quarterKey)?.label || quarterKey}</b>{' '}
              · Times shown in <b>{tzLabel(tableTz)}</b>
            </div>

            {availEditorError && (
              <div className="ogta-modal-error" role="alert">{availEditorError}</div>
            )}

            <div className="ogta-modal-body">
              <div className="ogta-modal-row">
                <div className="ogta-modal-label">Status</div>
                <div className="ogta-modal-status">
                  <button
                    type="button"
                    className={`ogta-pill ${availEditorDraft.status === 'available' ? 'is-available' : ''}`}
                    onClick={() => setAvailEditorDraft(p => ({ ...p, status: 'available' }))}
                  >
                    Available
                  </button>
                  <button
                    type="button"
                    className={`ogta-pill ${availEditorDraft.status === 'maybe' ? 'is-maybe' : ''}`}
                    onClick={() => setAvailEditorDraft(p => ({ ...p, status: 'maybe' }))}
                  >
                    Maybe
                  </button>
                  <button
                    type="button"
                    className={`ogta-pill ${availEditorDraft.status === 'unavailable' ? 'is-unavailable' : ''}`}
                    onClick={() => setAvailEditorDraft(p => ({ ...p, status: 'unavailable' }))}
                  >
                    Unavailable
                  </button>
                </div>
              </div>

              <div className="ogta-modal-row">
                <div className="ogta-modal-label">Time range</div>
                <div className="ogta-modal-time">
                  <input
                    className="admin-input ogta-time"
                    type="time"
                    value={availEditorDraft.start}
                    onChange={(e) => setAvailEditorDraft(p => ({ ...p, start: e.target.value }))}
                  />
                  <span className="ogta-ctrl-sep">–</span>
                  <input
                    className="admin-input ogta-time"
                    type="time"
                    value={availEditorDraft.end}
                    onChange={(e) => setAvailEditorDraft(p => ({ ...p, end: e.target.value }))}
                  />
                </div>
              </div>

              {availEditorDraft.status === 'maybe' && (
                <>
                  <div className="ogta-modal-row">
                    <div className="ogta-modal-label">Maybe reason</div>
                    <select
                      className="admin-select"
                      value={availEditorDraft.maybeReason || ''}
                      onChange={(e) => setAvailEditorDraft(p => ({ ...p, maybeReason: (e.target.value || null) as any }))}
                    >
                      <option value="">Select…</option>
                      {MAYBE_REASONS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ogta-modal-row">
                    <div className="ogta-modal-label">Maybe note</div>
                    <input
                      className="admin-input"
                      type="text"
                      placeholder="Optional context…"
                      value={availEditorDraft.maybeNote}
                      onChange={(e) => setAvailEditorDraft(p => ({ ...p, maybeNote: e.target.value }))}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="ogta-modal-actions">
              <button type="button" className="ogta-btn" onClick={closeAvailabilityEditor} disabled={availEditorSaving}>
                Cancel
              </button>
              <button type="button" className="ogta-btn ogta-btn-primary" onClick={saveAvailabilityEditor} disabled={availEditorSaving}>
                {availEditorSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAvailabilityAdminPage;
